// MARK://dhk8/pos/lib/clients/petty-cash
// Typed client for dhk8-gastos petty-cash — compra pagada con efectivo del cajón.
// Reconciliado contra services/gastos/src/server.ts y
// services/gastos/src/repositories/gastos.repository.ts (DTOs).
//
// Ingress route: /v1/dhk8-gastos/v1/gastos/petty-cash-purchases → el servicio ve
// /v1/gastos/petty-cash-purchases.
//
// Invariante del servidor (422 VALIDATION si no cuadra):
//   withdrawnCents − changeCents + (cashierContributedCents ?? 0) === Σ lines.totalCents
//
// Cada línea `capitalizable:true` se materializa como StockLot a costo real
// (activo/inventario); `false` → gasto. El correlador con los cortes de caja va
// por cashDropId / cashPickupId (+ idempotencyKey = purchaseRef del flujo POS).
//
// ── Ciclo 2-fases (rediseño petty-cash en el POS) ────────────────────────────
// FASE 1 (retiro mínimo, CashoutPage): `openRetiroCompra` — abre el
// PettyCashPurchase (OPEN) sin líneas; NO materializa nada, NO valida identidad.
// FASE 2 (conciliación diferida, PettyCashDetailPage): `reconcilePettyCashPurchase`
// — único punto de materialización (líneas + cambio + soportes → RECONCILED),
// el servidor SÍ valida la identidad de dinero aquí (422 si no cuadra).
// `cancelPettyCashPurchase` cierra un retiro abierto sin conciliar (OPEN→CANCELLED,
// no materializa nada — el reintegro del efectivo se registra en el POS como un
// CASH_PICKUP aparte, ver PettyCashDetailPage). `listPettyCashPurchases` /
// `getPettyCashPurchase` son lecturas (lista + detalle con líneas/soportes).

import { apiData, apiFetch, svcUrl } from '../api.js'

// ── Motivos de cortes de caja ligados a petty-cash ───────────────────────────
// NO son campos del backend gastos — son el `reason` libre que el POS pone en
// el CashCut (CASH_DROP/CASH_PICKUP de dhk8-pos-cashcuts) para poder filtrar el
// historial y correlacionar con el PettyCashPurchase (vía purchaseRef). Viven
// aquí (no en cashcuts.ts) por ser específicos del dominio petty-cash — así
// FASE 1 (CashoutPage) y FASE 2 (PettyCashDetailPage) comparten el mismo string.
export const PETTY_CASH_PURCHASE_REASON = 'PETTY_CASH_PURCHASE'
export const PETTY_CASH_CHANGE_REASON = 'PETTY_CASH_CHANGE'
export const PETTY_CASH_REFUND_REASON = 'PETTY_CASH_REFUND'

// ── Input (mirror de CreatePettyCashPurchaseInput / PettyCashLineInput) ──────────

export interface PettyCashLineInput {
  label: string
  qty: number
  unitCostCents: number
  totalCents: number
  /** true → va a inventario (StockLot, activo); false → gasto. */
  capitalizable: boolean
  productId?: string
  stockItemId?: string
  supplierId?: string
  warehouseId?: string
  lotNumber?: string
  expiryDate?: string
}

export interface CreatePettyCashPurchaseInput {
  withdrawnCents: number
  changeCents: number
  cashierContributedCents?: number
  branchId?: string
  cashierId?: string
  cashDropId?: string
  cashPickupId?: string
  supplierId?: string
  purchaseOrderId?: string
  notes?: string
  attachmentKeys?: string[]
  idempotencyKey?: string
  lines: PettyCashLineInput[]
}

// ── Output (mirror de PettyCashPurchaseDto / PettyCashPurchaseLineDto) ────────────

export interface PettyCashPurchaseLine {
  id: string
  purchaseId: string
  productId: string | null
  label: string
  capitalizable: boolean
  qty: number
  unitCostCents: number
  totalCents: number
  supplierId: string | null
  stockLotId: string | null
  expenseId: string | null
  createdAt: string
}

export interface PettyCashPurchaseAttachment {
  id: string
  purchaseId: string
  lineId: string | null
  supplierId: string | null
  storageKey: string
  contentType: string
  label: string | null
  createdAt: string
}

export interface PettyCashPurchaseResult {
  id: string
  tenantId: string
  branchId: string | null
  cashierId: string | null
  shiftId: string | null
  cashDropId: string | null
  cashPickupId: string | null
  withdrawnCents: number
  spentCents: number
  changeCents: number
  cashierContributedCents: number
  supplierId: string | null
  purchaseOrderId: string | null
  notes: string | null
  attachmentKeys: string[]
  status: string
  reconciledByCashierId: string | null
  reconciledAt: string | null
  cancelledAt: string | null
  cancelledReason: string | null
  idempotencyKey: string | null
  createdAt: string
  lines: PettyCashPurchaseLine[]
  attachments?: PettyCashPurchaseAttachment[]
}

/** Fila resumida para la lista (GET /v1/gastos/petty-cash-purchases) — sin líneas ni soportes. */
export interface PettyCashPurchaseSummary {
  id: string
  status: string
  withdrawnCents: number
  spentCents: number
  changeCents: number
  cashierContributedCents: number
  cashDropId: string | null
  cashPickupId: string | null
  branchId: string | null
  cashierId: string | null
  shiftId: string | null
  notes: string | null
  createdAt: string
  reconciledAt: string | null
  cancelledAt: string | null
}

/** Fase 1: abrir el retiro (sin líneas, sin materializar nada). */
export interface OpenPettyCashPurchaseInput {
  withdrawnCents: number
  idempotencyKey: string
  cashDropId?: string
  branchId?: string
  cashierId?: string
  shiftId?: string
  notes?: string
}

/** Soporte (comprobante) inline al conciliar. Sin lineId — las líneas se crean
 *  en este mismo paso, así que un adjunto sólo se asocia por `supplierId` (o a
 *  ninguna línea en particular). */
export interface PettyCashAttachmentInput {
  storageKey: string
  contentType: string
  supplierId?: string
  label?: string
}

/** Fase 2: conciliar el retiro abierto (líneas + cambio + soportes → RECONCILED). */
export interface ReconcilePettyCashPurchaseInput {
  lines: PettyCashLineInput[]
  changeCents: number
  cashierContributedCents?: number
  cashPickupId?: string
  supplierId?: string
  purchaseOrderId?: string
  notes?: string
  reconciledByCashierId?: string
  attachments?: PettyCashAttachmentInput[]
  idempotencyKey?: string
}

type Envelope<T> = { ok: true; data: T } | { ok: false; error: { code: string; message: string } }

const BASE = svcUrl('dhk8-gastos', '/v1/gastos/petty-cash-purchases')

/**
 * Registra una compra petty-cash (SÍNCRONO). El servidor valida la identidad
 * de dinero y responde 201 con la compra (+ sus líneas). Si el desglose no cuadra
 * (o falta algún campo obligatorio) el servicio responde 422 VALIDATION — aquí se
 * relanza como Error con el `message` del servicio para mostrarlo en la UI.
 */
export async function createPettyCashPurchase(input: CreatePettyCashPurchaseInput): Promise<PettyCashPurchaseResult> {
  // apiFetch lanza ApiError (con el message del envelope) en respuestas !ok,
  // así que un 422 llega aquí como throw — no hace falta chequear res.ok.
  const body = await apiFetch<Envelope<PettyCashPurchaseResult> | PettyCashPurchaseResult>(BASE, {
    method: 'POST',
    body: input,
  })
  if (body !== null && typeof body === 'object' && 'ok' in body) {
    const env = body as Envelope<PettyCashPurchaseResult>
    if (env.ok) return env.data
    throw new Error(env.error.message)
  }
  return body as PettyCashPurchaseResult
}

// ── Ciclo 2-fases ─────────────────────────────────────────────────────────────
// Los 5 endpoints de abajo SIEMPRE responden con el envelope `{ok,data}` /
// `{ok:false,error}` (ver server.ts) — `apiData` ya hace exactamente ese unwrap
// y relanza como ApiError con el `message` del servicio en 4xx (incluye el 422
// de identidad de dinero del reconcile). No hace falta repetir el chequeo manual
// que sí necesita `createPettyCashPurchase` (compat con la respuesta legacy).

/**
 * FASE 1 — abre el retiro de efectivo (sin líneas, sin materializar nada).
 * `idempotencyKey` debe ser estable entre reintentos (el POS usa el mismo
 * `purchaseRef` que liga el CASH_DROP con esta compra).
 */
export async function openRetiroCompra(input: OpenPettyCashPurchaseInput): Promise<PettyCashPurchaseResult> {
  return apiData<PettyCashPurchaseResult>(`${BASE}/open`, { method: 'POST', body: input })
}

/**
 * FASE 2 — concilia el retiro abierto: crea las líneas (materializa StockLot/gasto
 * por línea) y los soportes, y pasa la compra a RECONCILED. El servidor valida
 * la identidad `withdrawn − change + contributed === Σ totalCents` (422 si no
 * cuadra) — ver `services/gastos/src/repositories/gastos.repository.ts#conciliarCompra`.
 */
export async function reconcilePettyCashPurchase(
  id: string,
  input: ReconcilePettyCashPurchaseInput,
): Promise<PettyCashPurchaseResult> {
  return apiData<PettyCashPurchaseResult>(`${BASE}/${encodeURIComponent(id)}/reconcile`, { method: 'POST', body: input })
}

/** Cancela un retiro abierto sin conciliar (OPEN → CANCELLED). NO materializa
 *  nada — el reintegro del efectivo al cajón se registra aparte como CASH_PICKUP. */
export async function cancelPettyCashPurchase(id: string, reason: string): Promise<PettyCashPurchaseResult> {
  return apiData<PettyCashPurchaseResult>(`${BASE}/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    body: { reason },
  })
}

/** Lista de compras petty-cash (resumen, sin líneas/soportes). El POS pide
 *  `?status=OPEN` para los pendientes de conciliar. */
export async function listPettyCashPurchases(params?: {
  status?: string
  branchId?: string
  limit?: number
}): Promise<PettyCashPurchaseSummary[]> {
  const qs = new URLSearchParams()
  if (params?.status) qs.set('status', params.status)
  if (params?.branchId) qs.set('branchId', params.branchId)
  if (params?.limit) qs.set('limit', String(params.limit))
  const url = qs.toString().length > 0 ? `${BASE}?${qs.toString()}` : BASE
  return apiData<PettyCashPurchaseSummary[]>(url)
}

/** Detalle de una compra: compra + líneas + soportes. */
export async function getPettyCashPurchase(id: string): Promise<PettyCashPurchaseResult> {
  return apiData<PettyCashPurchaseResult>(`${BASE}/${encodeURIComponent(id)}`)
}
